import { Users, Heart, Settings, Bot, Info, Mail, HelpCircle, FileText, Shield, BookOpen, User, Gift, Clock, Calendar, ChevronLeft, ChevronRight, ChevronDown, Activity, Chrome, MessageCircleQuestion, Skull, Gamepad2, Brain, UserPlus } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigate, useLocation } from "react-router";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { ScrollArea } from "@/components/ui/scroll-area";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useState, useEffect } from "react";

export const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { translate } = useLanguage();
  const { user } = useAuth();
  
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });

  const [gamesOpen, setGamesOpen] = useState(() => {
    const gamesRoutes = ['/randomizer', '/friend-quiz', '/mafia', '/sync'];
    return gamesRoutes.some(route => location.pathname.startsWith(route));
  });

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', isCollapsed.toString());
  }, [isCollapsed]);

  useEffect(() => {
    const gamesRoutes = ['/randomizer', '/friend-quiz', '/mafia', '/sync'];
    if (gamesRoutes.some(route => location.pathname.startsWith(route))) {
      setGamesOpen(true);
    }
  }, [location.pathname]);

  const isActive = (href: string) => location.pathname === href;
  const isGamesActive = () => ['/randomizer', '/friend-quiz', '/mafia', '/sync'].some(route => location.pathname.startsWith(route));

  const menuItems = [
    { icon: User, label: "Profile", href: "/" },
    { icon: Heart, label: "Wishlist", href: "/wishlist" },
    { icon: Users, label: "Friends", href: "/friends" },
    { icon: UserPlus, label: "Referrals", href: "/referrals" },
    { icon: Clock, label: "Reminders", href: "/reminders" },
    { icon: Calendar, label: "Events", href: "/events" },
    { icon: Bot, label: "AI Assistant", href: "/ai-assistant" },
    { icon: Settings, label: "Settings", href: "/settings" },
  ];

  const gamesItems = [
    { icon: Gift, label: "Randomizer", href: "/randomizer" },
    { icon: MessageCircleQuestion, label: "Friend Quiz", href: "/friend-quiz" },
    { icon: Skull, label: "Mafia Game", href: "/mafia" },
    { icon: Brain, label: "Sync Game", href: "/sync" },
  ];

  const commonMenuItems = [
    { icon: Info, label: "About", href: "/about" },
    { icon: Activity, label: "Status", href: "https://status.buyfors.com", external: true },
    { icon: Mail, label: "Contact", href: "/contact" },
    { icon: HelpCircle, label: "FAQ", href: "/faq" },
    { icon: Shield, label: "Privacy Policy", href: "/privacy-policy" },
    { icon: FileText, label: "Terms of Service", href: "/terms-of-service" },
    { icon: BookOpen, label: "Impressum", href: "/impressum" },
  ];

  const handleNavigation = (href: string) => {
    if (location.pathname !== href) {
      navigate(href);
    }
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const renderMenuItem = (item: { icon: any; label: string; href: string; external?: boolean }, isMainMenu = true) => {
    const active = isActive(item.href);
    const buttonContent = item.external ? (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 group relative overflow-hidden",
          isMainMenu ? "text-gray-700 dark:text-gray-300" : "text-gray-600 dark:text-gray-400 py-2",
          "hover:bg-white/60 dark:hover:bg-gray-800/60 hover:text-primary hover:scale-[1.02]"
        )}
      >
        <div className={cn(
          "p-2 rounded-lg transition-all duration-300 flex items-center justify-center",
          isMainMenu ? "shadow-sm group-hover:shadow-md" : "",
          "bg-white/80 dark:bg-gray-800/80 group-hover:bg-gradient-to-br group-hover:from-primary/10 group-hover:to-primary/20"
        )}>
          <item.icon className={cn(
            "transition-all duration-300",
            isMainMenu ? "h-5 w-5" : "h-4 w-4",
            "group-hover:scale-110 group-hover:rotate-3"
          )} />
        </div>
        {!isCollapsed && (
          <span className={cn(
            "font-medium transition-all duration-300",
            isMainMenu ? "text-base" : "text-sm"
          )}>
            {item.label}
          </span>
        )}
      </a>
    ) : (
      <button
        onClick={() => handleNavigation(item.href)}
        className={cn(
          "w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 group relative overflow-hidden",
          isMainMenu ? "text-gray-700 dark:text-gray-300" : "text-gray-600 dark:text-gray-400 py-2",
          active && "bg-gradient-to-r from-primary/10 to-primary/5 text-primary font-semibold border-l-4 border-primary",
          !active && "hover:bg-white/60 dark:hover:bg-gray-800/60 hover:text-primary hover:scale-[1.02]"
        )}
      >
        <div className={cn(
          "p-2 rounded-lg transition-all duration-300 flex items-center justify-center",
          isMainMenu ? "shadow-sm group-hover:shadow-md" : "",
          active 
            ? "bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-lg shadow-primary/20" 
            : "bg-white/80 dark:bg-gray-800/80 group-hover:bg-gradient-to-br group-hover:from-primary/10 group-hover:to-primary/20"
        )}>
          <item.icon className={cn(
            "transition-all duration-300",
            isMainMenu ? "h-5 w-5" : "h-4 w-4",
            active && "scale-110",
            !active && "group-hover:scale-110 group-hover:rotate-3"
          )} />
        </div>
        {!isCollapsed && (
          <span className={cn(
            "font-medium transition-all duration-300",
            isMainMenu ? "text-base" : "text-sm",
            active && "translate-x-1"
          )}>
            {item.label}
          </span>
        )}
      </button>
    );

    if (isCollapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            {buttonContent}
          </TooltipTrigger>
          <TooltipContent side="right" className="font-medium">
            {item.label}
          </TooltipContent>
        </Tooltip>
      );
    }

    return buttonContent;
  };

  return (
    <TooltipProvider>
      <aside
        className={cn(
          "fixed left-0 top-0 h-full border-r border-gray-200/60 dark:border-gray-700/60 transform lg:translate-x-0 z-40 backdrop-blur-xl bg-gradient-to-b from-white/95 via-white/90 to-[#F1F0FB]/95 dark:from-gray-900/95 dark:via-gray-800/90 dark:to-gray-900/95 shadow-xl",
          "lg:top-16 lg:h-[calc(100vh-4rem)]",
          "hidden lg:block animate-fade-in transition-all duration-300 ease-in-out",
          isCollapsed ? "w-20" : "w-64"
        )}
      >
        <VisuallyHidden>
          <h2 id="sidebar-title">Navigation Menu</h2>
        </VisuallyHidden>
        
        <div className="flex flex-col h-full relative">
          {/* Collapse Toggle Button */}
          <button
            onClick={toggleCollapse}
            className="absolute -right-3 top-6 z-50 p-1.5 rounded-full bg-white dark:bg-gray-800 border-2 border-primary/20 shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 hover:bg-primary/5"
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4 text-primary" />
            ) : (
              <ChevronLeft className="h-4 w-4 text-primary" />
            )}
          </button>

          <div className="h-16 lg:h-0" />
          
          <ScrollArea className="flex-1 py-6">
            <div className="flex flex-col">
              <ul className="space-y-1 px-3" aria-labelledby="sidebar-title">
                {menuItems.slice(0, 5).map((item) => (
                  <li key={item.label}>
                    {renderMenuItem(item, true)}
                  </li>
                ))}
                
                {/* Games Collapsible Menu */}
                <li>
                  <Collapsible open={gamesOpen} onOpenChange={setGamesOpen}>
                    <CollapsibleTrigger asChild>
                      <button
                        className={cn(
                          "w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 group relative overflow-hidden text-gray-700 dark:text-gray-300",
                          isGamesActive() && "bg-gradient-to-r from-primary/10 to-primary/5 text-primary font-semibold",
                          !isGamesActive() && "hover:bg-white/60 dark:hover:bg-gray-800/60 hover:text-primary hover:scale-[1.02]"
                        )}
                      >
                        <div className={cn(
                          "p-2 rounded-lg transition-all duration-300 flex items-center justify-center shadow-sm group-hover:shadow-md",
                          isGamesActive() 
                            ? "bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-lg shadow-primary/20" 
                            : "bg-white/80 dark:bg-gray-800/80 group-hover:bg-gradient-to-br group-hover:from-primary/10 group-hover:to-primary/20"
                        )}>
                          <Gamepad2 className={cn(
                            "h-5 w-5 transition-all duration-300",
                            isGamesActive() && "scale-110",
                            !isGamesActive() && "group-hover:scale-110 group-hover:rotate-3"
                          )} />
                        </div>
                        {!isCollapsed && (
                          <>
                            <span className={cn(
                              "font-medium transition-all duration-300 text-base flex-1 text-left",
                              isGamesActive() && "translate-x-1"
                            )}>
                              Games
                            </span>
                            <ChevronDown className={cn(
                              "h-4 w-4 transition-transform duration-200",
                              gamesOpen && "rotate-180"
                            )} />
                          </>
                        )}
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-1 mt-1">
                      {gamesItems.map((item) => (
                        <div key={item.label} className={cn(isCollapsed ? "" : "pl-4")}>
                          {renderMenuItem(item, true)}
                        </div>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                </li>

                {menuItems.slice(5).map((item) => (
                  <li key={item.label}>
                    {renderMenuItem(item, true)}
                  </li>
                ))}
              </ul>

              <div className="mt-6 pt-6 border-t border-gradient-to-r from-transparent via-gray-200 to-transparent">
                {!isCollapsed && (
                  <h3 className="px-6 mb-3 text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent to-gray-300" />
                    <span>Pages</span>
                    <div className="h-px flex-1 bg-gradient-to-l from-transparent to-gray-300" />
                  </h3>
                )}
                <ul className="space-y-1 px-3">
                  {commonMenuItems.map((item) => (
                    <li key={item.label}>
                      {renderMenuItem(item, false)}
                    </li>
                  ))}
                </ul>
              </div>

              {user && (
                <div className="mt-6 pt-6 border-t border-gradient-to-r from-transparent via-gray-200 to-transparent">
                  <ul className="space-y-1 px-3">
                    <li>
                      {renderMenuItem({ icon: Chrome, label: "Install Extension", href: "https://chromewebstore.google.com/detail/fncfihbdgihnnjjjbjnejafcaliidleg", external: true }, false)}
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Decorative gradient overlay at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#F1F0FB]/50 dark:from-gray-900/50 to-transparent pointer-events-none" />
      </aside>
    </TooltipProvider>
  );
};
